import { B2Row } from "@hdb/biome-2";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import HasPermission from "src/core/guards/has-permission";
import SelectComponent from "src/shared-elements/select/select-component";
import TitleComponent from "src/shared/title/title.component";
import { getFilterBlocksBySiteCode } from "src/store/slices/location-block.slice";
import { getFilterSiteByTCCode } from "src/store/slices/location-site.slice";
import {
  getEnergySavingsDashboardDataByTcCode,
  getEnergySavingsGraphDataByTcCode,
  getLightLocationDashboardSummary,
  getLocationBlockSummary,
  getLocationGatewaySummary,
  getLocationLightSummary,
} from "../../../../core/api/services/dashboard.services";
import {
  setSelectedBlock,
  setSelectedSiteCode,
  setSelectedTcCode,
} from "../../../../store/slices/dashboard.slice";
import { getTownCouncils } from "../../../../store/slices/town-council.slice";
import EnergySavingInfoComponent from "../energy-saving-info.component";
import FaultInfoBox from "../fault-info-box/FaultInfoBox";
import FilterComponent from "../filter/filter.component";
import MapComponent from "../map.component";
import TCStatisticsComponent from "../tc-statistics.component";
import TurnAroundTime from "../turn-around-time/TurnAroundTime";

const DashboardMainComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    try {
      const postLoginRedirect = localStorage.getItem("postLoginRedirect");
      const currentPath = pathname;

      if (postLoginRedirect && postLoginRedirect !== currentPath) {
        localStorage.removeItem("postLoginRedirect");
        // window.location.href = postLoginRedirect;
        navigate(postLoginRedirect, { replace: true });
      } else {
        localStorage.removeItem("postLoginRedirect");
      }
    } catch (error) {
      console.error("Redirect handling failed:", error);
      localStorage.removeItem("postLoginRedirect");
    }
  }, [pathname]);

  const [energySavingGraphData, setEnergySavingGraphData] = useState<any>({
    xAxisData: [],
    seriesData: [],
    extraData: [],
  });

  const townCouncils = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const filterSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.filterSitesByTCCode;
  });

  const isLoadingFilterSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLoadingFilterSitesByTCCode;
  });

  const filterBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.filterBlocksBySiteCode;
  });

  const isLoadingFilterBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingFilterBlocksBySiteCode;
  });

  const [townCouncilsData, setTownCouncils] = useState([]);
  const [sitesData, setSites] = useState([]);
  const [blocksData, setBlocksData] = useState([]);

  const [firstTc, setFirstTc] = useState("");

  const selectedTcCode = useSelector(
    (state: any) => state.dashboard.selectedTcCode,
  );

  const selectedSiteCode = useSelector(
    (state: any) => state.dashboard.selectedSiteCode,
  );

  const selectedBlock = useSelector(
    (state: any) => state.dashboard.selectedBlock,
  );

  const selectedFaultType = useSelector(
    (state: any) => state.dashboard.selectedFaultType,
  );

  const {
    data: lightLocationDashboardSummary,
    isLoading: isLoadingLightLocationDashboardSummary,
  } = useQuery({
    queryKey: ["getLightLocationDashboardSummary", selectedTcCode],
    queryFn: ({ signal }) =>
      getLightLocationDashboardSummary(selectedTcCode, signal),
    enabled: !!selectedTcCode || !!firstTc,
  });

  const {
    data: locationBlockSummary,
    isLoading: isLoadingLocationBlockSummary,
  } = useQuery({
    queryKey: ["getLocationBlockSummary", selectedTcCode],
    queryFn: ({ signal }) => getLocationBlockSummary(selectedTcCode, signal),
    enabled: !!selectedTcCode || !!firstTc,
  });

  const {
    data: locationGatewaySummary,
    isLoading: isLoadingLocationGatewaySummary,
  } = useQuery({
    queryKey: ["getLocationGatewaySummary", selectedTcCode],
    queryFn: ({ signal }) => getLocationGatewaySummary(selectedTcCode, signal),
    enabled: !!selectedTcCode || !!firstTc,
  });

  const {
    data: locationLightSummary,
    isLoading: isLoadingLocationLightSummary,
  } = useQuery({
    queryKey: ["getLocationLightSummary", selectedTcCode],
    queryFn: ({ signal }) => getLocationLightSummary(selectedTcCode, signal),
    enabled: !!selectedTcCode || !!firstTc,
  });

  const { data: EnergySavingsData, isLoading: isLoadingEnergySavingsData } =
    useQuery({
      queryKey: ["getEnergySavingsDashboardDataByTcCode", selectedTcCode],
      queryFn: ({ signal }) =>
        getEnergySavingsDashboardDataByTcCode(selectedTcCode, signal),
      enabled: !!selectedTcCode || !!firstTc,
    });

  const { data: energyGraphData, isLoading: isLoadingEnergyGraphData } =
    useQuery({
      queryKey: ["getEnergySavingsGraphDataByTcCode", selectedTcCode],
      queryFn: ({ signal }) =>
        getEnergySavingsGraphDataByTcCode(selectedTcCode, signal),
      enabled: !!selectedTcCode,
    });

  useEffect(() => {
    if (energyGraphData?.length > 0) {
      const xData = energyGraphData?.map((d: any) => d?.month);
      const series = energyGraphData?.map((d: any) => d?.total_saved_energy);
      const extra = energyGraphData?.map((d: any) => d?.year);

      setEnergySavingGraphData({
        xAxisData: xData,
        seriesData: series,
        extraData: extra,
      });
    } else {
      setEnergySavingGraphData({
        xAxisData: [],
        seriesData: [],
        extraData: [],
      });
    }
  }, [energyGraphData]);

  useEffect(() => {
    if (!townCouncils.length) {
      dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
    }
  }, []);

  const [energySaving, setEnergySaving] = useState({
    lifetimesavingsinkwh: 0,
    savedforthismonthinkwh: 0,
    percentagechangeagainstprevmonth: 0,
    savingscoinkg: 0,
    savingsagainstnonsmartpercentage: 0,
  });

  useEffect(() => {
    if (!EnergySavingsData) return;
    setEnergySaving({
      lifetimesavingsinkwh: EnergySavingsData?.lifetimesavingsinkwh,
      savedforthismonthinkwh: EnergySavingsData?.savedforthismonthinkwh,
      savingsagainstnonsmartpercentage:
        EnergySavingsData?.savingsagainstnonsmartpercentage,
      savingscoinkg: EnergySavingsData?.savingscoinkg,
      percentagechangeagainstprevmonth:
        EnergySavingsData?.percentagechangeagainstprevmonth,
    });
  }, [EnergySavingsData]);

  useEffect(() => {
    if (selectedTcCode) {
      dispatch(getFilterSiteByTCCode({ tcCode: selectedTcCode }));
    } else {
      getFilterSiteByTCCode();
    }
  }, [selectedTcCode]);

  useEffect(() => {
    if (selectedSiteCode) {
      dispatch(getFilterBlocksBySiteCode({ siteCode: selectedSiteCode }));
    }
  }, [selectedSiteCode]);

  useEffect(() => {
    if (townCouncils?.data?.length) {
      const data: any = JSON.parse(JSON.stringify(townCouncils?.data));
      for (let item of data) {
        item["value"] = item?.tcCode;
        item["title"] = item?.name;
      }

      if (selectedTcCode) {
        setFirstTc(selectedTcCode);
      } else {
        if (data.length >= 10) {
          setFirstTc(data[7]?.tcCode);
        } else {
          setFirstTc(data[0]?.tcCode);
        }
      }
      setTownCouncils(data);
    }
  }, [townCouncils]);

  useEffect(() => {
    if (filterSitesByTCCode?.data?.length) {
      const data: any = JSON.parse(JSON.stringify(filterSitesByTCCode?.data));
      for (let item of data) {
        item["value"] = item.siteCode;
        item["title"] = item.name;
      }
      setSites(data);
    }
  }, [filterSitesByTCCode]);

  useEffect(() => {
    if (filterBlocksBySiteCode?.data?.length) {
      const data: any = JSON.parse(
        JSON.stringify(filterBlocksBySiteCode?.data),
      );
      for (let item of data) {
        item["value"] = item.blockCode;
        item["title"] = item.blockName;
      }
      setBlocksData(data);
    } else {
      setBlocksData([]);
    }
  }, [filterBlocksBySiteCode]);

  useEffect(() => {
    if (selectedTcCode) {
      dispatch(setSelectedTcCode(selectedTcCode));
    } else {
      dispatch(setSelectedTcCode(firstTc));
    }

    if (selectedSiteCode) {
      dispatch(setSelectedSiteCode(selectedSiteCode));
    } else {
      dispatch(setSelectedSiteCode(null));
    }

    // KEEP::
    /* if (selectedBlock) {
      dispatch(setSelectedBlock(selectedBlock));
    } else {
      dispatch(setSelectedBlock(null));
    } */

    dispatch(setSelectedBlock(null));
    setBlocksData([]);
  }, [firstTc]);

  const onChangeTC = (selectedValue: any) => {
    setSites([]);
    setBlocksData([]);

    dispatch(setSelectedSiteCode(null));
    dispatch(setSelectedBlock(null));
    dispatch(setSelectedTcCode(selectedValue ? selectedValue : null));
    setEnergySaving({
      lifetimesavingsinkwh: 0,
      savedforthismonthinkwh: 0,
      percentagechangeagainstprevmonth: 0,
      savingscoinkg: 0,
      savingsagainstnonsmartpercentage: 0,
    });
  };

  const onChangeSiteName = (selectedValue: any) => {
    if (!selectedValue) {
      setBlocksData([]);
    }

    dispatch(setSelectedSiteCode(selectedValue));
    dispatch(setSelectedBlock(null));
  };

  const onChangeBlock = (selectedValue: any) => {
    dispatch(setSelectedBlock(selectedValue));

    navigate(`/dashboard/${btoa(selectedValue)}`);
  };

  // Detect page refresh/closing
  /*   useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("postRefreshRedirect", pathname);
      navigate("/", { replace: true });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [pathname]); */

  return (
    <div>
      <Helmet>
        <title>Dashboard | SLP</title>
      </Helmet>
      <div className="page">
        <div className="page-container mt-3">
          <div style={{ width: "100%" }}>
            <div className="col-12 col-sm-6 col-lg-4">
              <SelectComponent
                selectedValue={selectedTcCode}
                options={townCouncilsData}
                placeHolder="Select Town Council"
                onChange={onChangeTC}
                isShowLabel={false}
              />
            </div>
          </div>

          <div className="">
            <TitleComponent title="" />
          </div>

          <B2Row className="mb-lg-3 dashboard-row">
            <TCStatisticsComponent
              lightLocationDashboardSummary={lightLocationDashboardSummary}
              isLoadingLightLocationDashboardSummary={
                isLoadingLightLocationDashboardSummary
              }
              isLoadingLocationBlockSummary={isLoadingLocationBlockSummary}
              locationBlockSummary={locationBlockSummary}
              isLoadingLocationGatewaySummary={isLoadingLocationGatewaySummary}
              locationGatewaySummary={locationGatewaySummary}
              isLoadingLocationLightSummary={isLoadingLocationLightSummary}
              locationLightSummary={locationLightSummary}
            />
            <EnergySavingInfoComponent
              energySaving={energySaving}
              energySavingGraphData={energySavingGraphData}
              isLoadingEnergyGraphData={isLoadingEnergyGraphData}
              isLoadingEnergySavingsData={isLoadingEnergySavingsData}
            />
          </B2Row>

          <B2Row className="mb-lg-3 dashboard-row">
            <FaultInfoBox />
            <TurnAroundTime />
          </B2Row>

          <HasPermission
            moduleName="Dashboard"
            isRoute={false}
            actionName="View-Dashboard"
          >
            {" "}
            <div className="position-relative">
              <MapComponent
                selectedTcCode={selectedTcCode}
                selectedSiteCode={selectedSiteCode}
                selectedFaultType={selectedFaultType}
                selectedBlock={selectedBlock}
              />
              {townCouncilsData?.length ? (
                <div
                  style={{
                    width: "40%",
                    position: "absolute",
                    marginLeft: "1rem",
                    zIndex: 999,
                    top: "1.25rem",
                  }}
                >
                  <FilterComponent
                    options={{ sitesData, blocksData }}
                    events={{ onChangeSiteName, onChangeBlock }}
                    isLoading={{
                      isLoadingFilterSitesByTCCode,
                      isLoadingFilterBlocksBySiteCode,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </HasPermission>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DashboardMainComponent);
